/**
 * The `min_price` field a calendar day is read from for a leg: `pricing` one way, the round-trip
 * fare of the leg otherwise.
 *
 * @param {string} priceWay - Calendar leg: departure or return.
 * @param {boolean} isRoundTrip - Whether the search is round trip.
 * @returns {string} The field name.
 */
const pricingField = (priceWay, isRoundTrip) => {
  if (!isRoundTrip) return 'pricing';
  return priceWay === 'departure' ? 'departure_round_trip_pricing' : 'round_trip_pricing';
};

/**
 * Resolves the calendar price for the active provider and trip leg.
 * Falls back to the API display price when provider pricing is unavailable.
 * @param {object} params - Calendar price params.
 * @param {object} params.calendarDay - Calendar day response.
 * @param {string} params.providerId - Provider selected in the route.
 * @param {string} params.priceWay - Calendar leg: departure or return.
 * @param {boolean} params.isRoundTrip - Whether the search is round trip.
 * @returns {number} Price to display in the carousel.
 */
export const getCalendarDayPrice = ({ calendarDay, providerId, priceWay, isRoundTrip }) => {
  const fallbackPrice = calendarDay?.display_price ?? 0;
  const providerPrices = providerId ? calendarDay?.min_price?.lines?.[providerId] : null;

  if (!providerPrices) return fallbackPrice;

  return providerPrices[pricingField(priceWay, isRoundTrip)] ?? fallbackPrice;
};

/**
 * The markup-inclusive twin of `getCalendarDayPrice` (BOOK-176), read from the same place: the
 * selected line's `<field>_with_markup`, or the day's `display_price_with_markup`. Each falls back to
 * the base price `getCalendarDayPrice` reads there, so a line without a companion never borrows the
 * day-wide one.
 *
 * @param {object} params - Calendar price params, as for `getCalendarDayPrice`.
 * @param {object} params.calendarDay - Calendar day response.
 * @param {string} params.providerId - Provider selected in the route.
 * @param {string} params.priceWay - Calendar leg: departure or return.
 * @param {boolean} params.isRoundTrip - Whether the search is round trip.
 * @returns {number} Price to display in the final arm.
 */
export const getCalendarDayFinalPrice = ({ calendarDay, providerId, priceWay, isRoundTrip }) => {
  const basePrice = getCalendarDayPrice({ calendarDay, providerId, priceWay, isRoundTrip });
  const providerPrices = providerId ? calendarDay?.min_price?.lines?.[providerId] : null;
  const companion = providerPrices
    ? providerPrices[`${pricingField(priceWay, isRoundTrip)}_with_markup`]
    : calendarDay?.display_price_with_markup;

  return companion ?? basePrice;
};
